import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import theme from '../theme';
import Images from '../assets/Images'; // Import Images
import ProbeterminButton from './ProbeterminButton'; // Import ProbeterminButton
import texts from '../locales/texts';
import LoginPopup from './LoginPopup';
import LogoutPopup from './LogoutPopup';
import Popup from './Popup';
import { useAuth } from '../AuthContext';

// Placeholder avatar for user profile
const Avatar = () => (
  <div style={{ 
    width: '32px', 
    height: '32px', 
    borderRadius: '50%', 
    backgroundColor: theme.colors.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold'
  }}>
    A
  </div>
);

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  border-radius: 16px;
  overflow: visible;
  position: relative;
  height: 70px;
  margin-bottom: 125px;

  @media (max-width: 980px) {
    height: 0px;
    margin-bottom: 100px;
  }
`;

const MobileHeaderContainer = styled(HeaderContainer)`
  flex-direction: column;
  height: auto;
  padding-bottom: 16px;
`;

const MobileTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 70px;
`;

const MobileButtonWrapper = styled.div`
  margin-top: 5px;
  transform: scale(0.85);
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-left: -15px;
`;

const LogoImage = styled.img`
  height: 300px;
  width: auto;
  transform: translateY(-5px) translateX(-13px);
  

  @media (max-width: 980px) {
    height: 240px;
    transform: translateY(-3px) translateX(-8px);
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 24px;
  align-items: center;
  height: 100%;
`;

const MobileNav = styled.nav`
  position: absolute;
  top: 70px;
  right: 0;
  left: 0;
  background-color: white;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 2000;
  transition: transform ${props => props.isOpen ? '0.3s' : '0.6s'} ease, opacity ${props => props.isOpen ? '0.3s' : '0.5s'} ease;
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'all' : 'none'};
`;

const MobileButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.active ? props.theme.colors.primary : '#757575'};
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  padding: 8px 12px;
  border-radius: 12px;
  background-color: ${props => props.active ? props.theme.colors.accent2 : 'transparent'};
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background-color: ${props => props.active ? props.theme.colors.accent2 : 'rgba(188, 231, 253, 0.4)'};
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuBreakpoint = 980; // Custom breakpoint for menu
  const [isMobile, setIsMobile] = useState(window.innerWidth <= menuBreakpoint);
  const [loginPopupOpen, setLoginPopupOpen] = useState(false);
  const [logoutPopupOpen, setLogoutPopupOpen] = useState(false);
  const [trialLessonPopupOpen, setTrialLessonPopupOpen] = useState(false);
  const { logout, isAuthenticated, user, hasRole } = useAuth();
  const navigate = useNavigate();

  // Preload owner images before logo loads
  useEffect(() => {
    const imgAvif = new Image();
    imgAvif.src = Images.ownerAvif;
    const imgWebp = new Image();
    imgWebp.src = Images.ownerWebp;
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= menuBreakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuBreakpoint]);
  
  const toggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const currentPath = window.location.pathname;

  const handleLogout = () => {
    logout();
    setLogoutPopupOpen(false);
    if (window.location.pathname === '/mypage' || window.location.pathname === '/meys-page') {
      navigate('/');
    }
  };

  const handleOpenTrialLessonPopup = () => {
    setTrialLessonPopupOpen(true);
  };

  const handleCloseTrialLessonPopup = () => {
    setTrialLessonPopupOpen(false);
  };

  const authed = isAuthenticated();

  if (isMobile) {
    return (
      <HeaderContainer>
        <Logo to="/">
          <LogoImage src={Images.mainLogo} alt="MeyTeaches Logo" />
        </Logo>
        <RightSection>
          <IconButton onClick={toggleMenu}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 18H21V16H3V18ZM3 13H21V11H3V13ZM3 6V8H21V6H3Z" fill={theme.colors.primary}/>
            </svg>
          </IconButton>
        </RightSection>
        <MobileNav isOpen={mobileMenuOpen}>
          <NavLink to="/" active={currentPath === '/'} onClick={() => setMobileMenuOpen(false)}>{texts.home}</NavLink>
          <NavLink to="/about-me" active={currentPath === '/about-me'} onClick={() => setMobileMenuOpen(false)}>{texts.aboutMe}</NavLink>
          <NavLink to="/program" active={currentPath === '/program'} onClick={() => setMobileMenuOpen(false)}>{texts.program}</NavLink>
          <NavLink to="/faq" active={currentPath === '/faq'} onClick={() => setMobileMenuOpen(false)}>{texts.faq}</NavLink>
          {!authed && (
            <NavLink to="#" onClick={() => { handleOpenTrialLessonPopup(); setMobileMenuOpen(false); }}>{texts.appointment}</NavLink>
          )}
          {!authed && (
            <NavLink to="#" onClick={() => { setLoginPopupOpen(true); setMobileMenuOpen(false); }}>{texts.login}</NavLink>
          )}
          {authed && hasRole('USER') && (
            <NavLink to="/mypage" active={currentPath === '/mypage'} onClick={() => setMobileMenuOpen(false)}>{texts.mypage}</NavLink>
          )}
          {authed && hasRole('ADMIN') && (
            <NavLink to="/meys-page" active={currentPath === '/meys-page'} onClick={() => setMobileMenuOpen(false)}>Meys Bereich</NavLink>
          )}
          {authed && (
            <NavLink to="#" onClick={() => { setLogoutPopupOpen(true); setMobileMenuOpen(false); }}>{texts.logout}</NavLink>
          )}
        </MobileNav>
        <LoginPopup isOpen={loginPopupOpen} onClose={() => setLoginPopupOpen(false)} />
        <LogoutPopup isOpen={logoutPopupOpen} onClose={() => setLogoutPopupOpen(false)} onLogout={handleLogout} />
        <Popup 
          isOpen={trialLessonPopupOpen} 
          onClose={handleCloseTrialLessonPopup}
          title="Probestunde"
        />
      </HeaderContainer>
    );
  }

  return (
    <HeaderContainer>
      <Logo to="/">
        <LogoImage src={Images.mainLogo} alt="MeyTeaches Logo" />
      </Logo>
      <Nav>
        <NavLink to="/" active={currentPath === '/'}>{texts.home}</NavLink>
        <NavLink to="/about-me" active={currentPath === '/about-me'}>{texts.aboutMe}</NavLink>
        <NavLink to="/program" active={currentPath === '/program'}>{texts.program}</NavLink>
        <NavLink to="/faq" active={currentPath === '/faq'}>{texts.faq}</NavLink>
        {!authed && (
          <NavLink to="#" onClick={handleOpenTrialLessonPopup}>{texts.appointment}</NavLink>
        )}
        {!authed && (
          <NavLink to="#" onClick={() => setLoginPopupOpen(true)}>{texts.login}</NavLink>
        )}
        {authed && hasRole('USER') && (
          <NavLink to="/mypage" active={currentPath === '/mypage'}>{texts.mypage}</NavLink>
        )}
        {authed && hasRole('ADMIN') && (
          <NavLink to="/meys-page" active={currentPath === '/meys-page'}>Meys Bereich</NavLink>
        )}
        {authed && (
          <NavLink to="#" onClick={() => setLogoutPopupOpen(true)}>{texts.logout}</NavLink>
        )}
      </Nav>
      <RightSection>
      </RightSection>
      <LoginPopup isOpen={loginPopupOpen} onClose={() => setLoginPopupOpen(false)} />
      <LogoutPopup isOpen={logoutPopupOpen} onClose={() => setLogoutPopupOpen(false)} onLogout={handleLogout} />
      <Popup 
        isOpen={trialLessonPopupOpen} 
        onClose={handleCloseTrialLessonPopup}
        title="Probestunde"
      />
    </HeaderContainer>
  );
};

export default Header; 